import { Link } from "react-router-dom";
import {
  GitFork, MessageSquare, ShieldQuestion, FileText, Pencil,
  Activity, Scale, Sparkles, Bell,
} from "lucide-react";
import { useStore } from "@/store/useStore";
import { timeAgo } from "@/lib/vocabulary";
import { EmptyState } from "@/components/AppShell";

export default function Notifications() {
  const strains = useStore((s) => s.strains);
  const notifications = strains.slice(0, 8).map((strain, i) => {
    const templates = [
      { type: "fork", icon: GitFork, tone: "primary", text: `${strain.activity?.forks ?? 0} forks are active on "${strain.title}"` },
      { type: "comment", icon: MessageSquare, tone: "muted", text: `${strain.activity?.comments ?? 0} comments are anchored across "${strain.title}"` },
      { type: "challenge", icon: ShieldQuestion, tone: "danger", text: `A crux on "${strain.title}" has a new challenge` },
      { type: "evidence", icon: FileText, tone: "info", text: `"${strain.title}" has evidence requests to review` },
      { type: "rewording", icon: Pencil, tone: "amber", text: `A bridge proposal is active on "${strain.title}"` },
      { type: "crux", icon: Scale, tone: "amber", text: `${strain.top_crux_label ?? "A crux"} is drawing attention` },
      { type: "mind_change", icon: Activity, tone: "inferred", text: `${strain.activity?.mind_changes ?? 0} mind-change signals on "${strain.title}"` },
      { type: "inferred", icon: Sparkles, tone: "inferred", text: `Inferred nodes are waiting for review on "${strain.title}"` },
    ];
    return { ...templates[i % templates.length], target: strain.slug, time: strain.activity?.last_activity ?? strain.updated_at };
  });
  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Activity</h1>
        <p className="text-[13px] text-[var(--color-ink-muted)] mt-0.5">What's happened across your strains.</p>
      </div>

      {notifications.length === 0 ? (
        <EmptyState icon={<Bell size={28} />} title="No activity yet" desc="When people interact with your strains, it shows up here." />
      ) : (
        <div className="space-y-1.5">
          {notifications.map((n, i) => {
            const Icon = n.icon;
            const color = n.tone === "primary" ? "var(--color-primary)"
              : n.tone === "danger" ? "var(--color-danger)"
              : n.tone === "amber" ? "var(--color-secondary)"
              : n.tone === "info" ? "var(--color-define)"
              : n.tone === "inferred" ? "var(--color-inferred)"
              : "var(--color-ink-muted)";
            return (
              <Link
                key={i}
                to={`/s/${n.target}`}
                className="flex items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 hover:border-[var(--color-primary)] transition-colors"
              >
                <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: `color-mix(in srgb, ${color} 12%, transparent)`, color }}>
                  <Icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[var(--color-ink-soft)] leading-snug">{n.text}</div>
                  <div className="text-[11px] text-[var(--color-ink-faint)] mt-0.5 font-mono">{timeAgo(n.time)}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
